/**
 * TeamManagementPage Component
 * 
 * Allows store admins to manage staff users - add, edit, delete, reset passwords.
 * Staff members can access the system but with limited permissions.
 * NOTE: Behavior copied from legacy App.js, do not change without regression testing.
 * 
 * @component
 * @param {Object} props
 * @param {Object} props.currentUser - Current logged-in user
 * @param {Object} props.currentStore - Current store data
 * @param {Function} props.onBack - Callback to navigate back
 */

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Users, Home, Edit, Trash, Trash2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../utils/constants';
import { getImageUrl } from '../../utils/formatters';

function TeamManagementPage({ currentUser, currentStore, onBack }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  
  const [newUser, setNewUser] = useState({
    name: '',
    username: '',
    email: '',
    role: 'store_staff', // Default to staff
    password: ''
  });
  
  // User management states
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [resettingPasswordUser, setResettingPasswordUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  
  // Password visibility states
  const [showAddUserPassword, setShowAddUserPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  
  // Ref for dropdown click-outside detection
  const userMenuRef = React.useRef(null);
  const buttonRefs = useRef({});

  useEffect(() => {
    loadUsers();
  }, []);
  
  // Click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      // Check if click is on the button
      const clickedButton = Object.values(buttonRefs.current).some(
        ref => ref && ref.contains(event.target)
      );
      if (clickedButton) return;
      
      // Check if click is inside the dropdown portal
      const dropdownPortal = document.querySelector('[data-team-dropdown]');
      if (dropdownPortal && dropdownPortal.contains(event.target)) return;
      
      // Close if clicked outside
      setSelectedUserId(null);
    }
    
    if (selectedUserId) {
      // Use setTimeout to avoid immediate close on the same click
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [selectedUserId]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      // Get all users for this store
      const response = await axios.get(`${API}/auth/users?storeId=${currentUser.storeId || currentStore?.storeId}`, {
        headers: {
          'X-User-Role': currentUser.role,
        },
      });
      setUsers(response.data || []);
    } catch (error) {
      console.error('Failed to load users:', error);
      // For now, show empty list if endpoint doesn't exist yet
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    try {
      if (!newUser.name || !newUser.password) {
        toast.error('Name and password are required');
        return;
      }

      // Both Admin and Staff users require username
      if (!newUser.username) {
        toast.error('Username is required');
        return;
      }

      if (newUser.password.length < 8) {
        toast.error('Password must be at least 8 characters');
        return;
      }

      setLoading(true);
      
      await axios.post(`${API}/auth/register`, {
        name: newUser.name,
        username: newUser.username,
        email: '',
        phone: '',
        password: newUser.password,
        role: newUser.role,
        storeId: currentUser.storeId || currentStore?.storeId
      });

      toast.success('Team member added successfully');
      setShowAddUser(false);
      setNewUser({
        name: '',
        username: '',
        email: '',
        role: 'store_staff',
        password: ''
      });
      loadUsers();
    } catch (error) {
      console.error('Failed to add user:', error);
      toast.error(error.response?.data?.detail || 'Failed to add team member');
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      store_owner: 'bg-orange-100 text-orange-800',
      store_admin: 'bg-purple-100 text-purple-800',
      store_staff: 'bg-blue-100 text-blue-800',
      customer: 'bg-gray-100 text-gray-800'
    };
    return badges[role] || 'bg-gray-100 text-gray-800';
  };

  const getRoleLabel = (role) => {
    const labels = {
      store_owner: 'Owner',
      store_admin: 'Manager',
      store_staff: 'Staff',
      customer: 'Customer'
    };
    return labels[role] || role;
  };
  
  const handleDeleteUser = async () => {
    if (!deletingUser) return;
    
    try {
      setLoading(true);
      await axios.delete(`${API}/auth/users/${deletingUser.id}`, {
        headers: {
          'X-User-Role': currentUser.role,
        },
      });
      toast.success('User deleted successfully');
      setDeletingUser(null);
      loadUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };
  
  // Check if a string looks like an email
  const isEmailUsername = (username) => {
    if (!username) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username);
  };
  
  const canDeleteUser = (user) => {
    // Prevent self-deletion
    if (user.id === currentUser.id) return false;
    
    // Super Admin can delete anyone
    if (currentUser.role === 'super_admin') return true;
    
    // Store Owner can delete users with non-email usernames (store_admin/store_staff)
    // Store Owner users (who login with email) cannot be deleted by other store owners
    if (currentUser.role === 'store_owner') {
      // Cannot delete other store_owners (they have email-based login)
      if (user.role === 'store_owner') return false;
      
      // Can delete store_admin and store_staff (username-based login)
      // Extra safety: check if their identifier looks like an email
      const userIdentifier = user.email || user.username;
      if (isEmailUsername(userIdentifier)) return false;
      
      return true;
    }
    
    return false;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <div className="nav-bawarchi">
        <div className="nav-brand flex items-center gap-2">
          {currentStore?.logoUrl && (
            <img src={getImageUrl(currentStore.logoUrl)} alt="Store Logo" className="h-16 w-auto" />
          )}
          <div className="flex flex-col">
            <span className="font-semibold">{currentStore?.name || 'CaterLog'}</span>
            {currentStore?.tagline && (
              <span className="text-sm text-gray-600">{currentStore.tagline}</span>
            )}
          </div>
        </div>
        <button onClick={onBack} className="btn-bawarchi-secondary" style={{ padding: '8px 20px', minHeight: '40px' }}>
          <Home className="w-4 h-4 mr-2 inline" />
          Back to Dashboard
        </button>
      </div>

      <div className="page-container">
        <div className="page-header text-center">
          <h1 className="page-title">Team Management</h1>
          <p className="page-subtitle">
            {currentStore?.name ? `${currentStore.name} - ` : ''}Manage your store&apos;s users
          </p>
          <div className="mt-4 flex justify-center">
            <button 
              type="button"
              onClick={() => setShowAddUser(!showAddUser)}
              className="btn-bawarchi-primary flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Team Member
            </button>
          </div>
        </div>

        {/* Add User Form */}
        {showAddUser && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Add New Team Member</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label-bawarchi">Full Name *</label>
                  <input
                    type="text"
                    className="input-bawarchi"
                    value={newUser.name}
                    onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                    placeholder="John Doe"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="label-bawarchi">Role *</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                    className="input-bawarchi"
                    disabled={loading}
                  >
                    <option value="store_staff">Staff (View only)</option>
                    <option value="store_admin">Manager (Can edit confirmed orders)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Staff: View orders only | Manager: View + Edit confirmed orders
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="label-bawarchi">Username * (for login)</label>
                  <input
                    type="text"
                    className="input-bawarchi"
                    value={newUser.username}
                    onChange={(e) => setNewUser({...newUser, username: e.target.value.toLowerCase().replace(/\s+/g, '')})}
                    placeholder={`${currentStore?.name?.toLowerCase().replace(/\s+/g, '_') || 'store'}_${newUser.role === 'store_staff' ? 'staff' : 'mgr'}_john`}
                    disabled={loading}
                  />
                  <p className="text-xs text-orange-600 font-medium mt-1">⚠️ Username must be globally unique across ALL stores!</p>
                  <p className="text-xs text-gray-500 mt-1">Suggested format: {currentStore?.name?.toLowerCase().replace(/\s+/g, '_') || 'yourstore'}_{newUser.role === 'store_staff' ? 'staff' : 'mgr'}_name</p>
                </div>
                <div className="md:col-span-2">
                  <label className="label-bawarchi">Temporary Password * (min 8 characters)</label>
                  <div className="relative">
                    <input
                      type={showAddUserPassword ? "text" : "password"}
                      className="input-bawarchi pr-10"
                      value={newUser.password}
                      onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                      placeholder="Create temporary password"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowAddUserPassword(!showAddUserPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showAddUserPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <p className="text-xs text-muted mt-1">
                    User will be able to change this after first login
                  </p>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button 
                  type="button"
                  onClick={handleAddUser} 
                  disabled={loading}
                  className="btn-bawarchi-primary"
                >
                  {loading ? 'Adding...' : 'Add Team Member'}
                </button>
                <button 
                  type="button"
                  onClick={() => setShowAddUser(false)} 
                  disabled={loading}
                  className="btn-bawarchi-secondary"
                >
                  Cancel
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Users List */}
        <Card className="overflow-visible">
          <CardHeader>
            <CardTitle>Team Members ({users.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading && users.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Loading team members...</p>
            ) : users.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No team members yet. Add your first team member!</p>
              </div>
            ) : (
              <div className="overflow-x-auto relative" style={{ maxHeight: '420px', overflowY: 'auto' }}>
                <table className="w-full" style={{ overflow: 'visible' }}>
                  <thead style={{ overflow: 'visible' }}>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Name</th>
                      <th className="text-left py-3 px-4">Login</th>
                      <th className="text-left py-3 px-4">Phone</th>
                      <th className="text-left py-3 px-4">Role</th>
                      <th className="text-left py-3 px-4">Last Login</th>
                      <th className="text-left py-3 px-4">Status</th>
                      <th className="text-left py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody style={{ overflow: 'visible' }}>
                    {users.map((user, index) => (
                      <tr key={user.id} className="border-b hover:bg-gray-50" style={{ overflow: 'visible' }}>
                        <td className="py-3 px-4 font-medium">{user.name}</td>
                        <td className="py-3 px-4 text-sm">
                          {user.email ? (
                            <span className="text-blue-600">{user.email}</span>
                          ) : (
                            <span className="font-mono text-green-600">@{user.username}</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm">{user.phone || '-'}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadge(user.role)}`}>
                            {getRoleLabel(user.role)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="relative" ref={selectedUserId === user.id ? userMenuRef : null}>
                            <button
                              ref={(el) => (buttonRefs.current[user.id] = el)}
                              onClick={(e) => {
                                if (selectedUserId === user.id) {
                                  setSelectedUserId(null);
                                } else {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setDropdownPosition({
                                    top: rect.bottom + window.scrollY + 4,
                                    left: rect.right + window.scrollX - 180, // 180px is dropdown width
                                  });
                                  setSelectedUserId(user.id);
                                }
                              }}
                              className="p-2 hover:bg-gray-100 rounded"
                              style={{ minHeight: '44px', minWidth: '44px' }}
                            >
                              ⋮
                            </button>
                            {selectedUserId === user.id && ReactDOM.createPortal(
                              <div
                                data-team-dropdown="true"
                                className="fixed bg-white rounded-lg shadow-lg border border-gray-200 min-w-[180px] z-[9999]"
                                style={{
                                  top: `${dropdownPosition.top}px`,
                                  left: `${dropdownPosition.left}px`,
                                }}
                              >
                                <button
                                  onClick={() => {
                                    setViewingUser(user);
                                    setSelectedUserId(null);
                                  }}
                                  className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2 rounded-t-lg"
                                >
                                  <Eye className="w-4 h-4" />
                                  View User
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingUser(user);
                                    setSelectedUserId(null);
                                  }}
                                  className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <Edit className="w-4 h-4" />
                                  Edit User
                                </button>
                                <button
                                  onClick={() => {
                                    setResettingPasswordUser(user);
                                    setSelectedUserId(null);
                                  }}
                                  className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <span className="w-4 h-4">🔑</span>
                                  Reset Password
                                </button>
                                {canDeleteUser(user) && (
                                  <button
                                    onClick={() => {
                                      setDeletingUser(user);
                                      setSelectedUserId(null);
                                    }}
                                    className="w-full text-left px-4 py-2 hover:bg-red-50 text-red-600 flex items-center gap-2 border-t rounded-b-lg"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    Delete User
                                  </button>
                                )}
                              </div>,
                              document.body
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* View User Modal */}
      {viewingUser && (
        <Dialog open={!!viewingUser} onOpenChange={() => setViewingUser(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>User Details</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div><strong>Name:</strong> {viewingUser.name}</div>
              <div><strong>Login:</strong> {viewingUser.email || `@${viewingUser.username}`}</div>
              <div><strong>Phone:</strong> {viewingUser.phone || '-'}</div>
              <div><strong>Role:</strong> {getRoleLabel(viewingUser.role)}</div>
              <div><strong>Store:</strong> {currentStore?.name}</div>
              <div><strong>Last Login:</strong> {viewingUser.last_login ? new Date(viewingUser.last_login).toLocaleString() : 'Never'}</div>
              <div><strong>Status:</strong> {viewingUser.is_active ? 'Active' : 'Inactive'}</div>
            </div>
            <Button onClick={() => setViewingUser(null)} className="mt-4">Close</Button>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Edit User Modal */}
      {editingUser && (
        <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="label-bawarchi">Name</label>
                <input
                  type="text"
                  className="input-bawarchi"
                  value={editingUser.name}
                  onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
                />
              </div>
              <div>
                <label className="label-bawarchi">Username *</label>
                <input
                  type="text"
                  className="input-bawarchi"
                  value={editingUser.username || ''}
                  onChange={(e) => setEditingUser({...editingUser, username: e.target.value})}
                  placeholder={`${currentStore?.name?.toLowerCase().replace(/\s+/g, '_') || 'store'}_${editingUser.role === 'store_staff' ? 'staff' : 'admin'}`}
                />
                <p className="text-xs text-gray-500 mt-1">Must be globally unique across all stores</p>
              </div>
              <div>
                <label className="label-bawarchi">Role</label>
                <select
                  className="input-bawarchi"
                  value={editingUser.role}
                  onChange={(e) => setEditingUser({...editingUser, role: e.target.value})}
                >
                  <option value="store_staff">Staff</option>
                  <option value="store_admin">Admin</option>
                </select>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={async () => {
                    try {
                      if (!editingUser) return;
                      await axios.put(
                        `${API}/auth/users/${editingUser.id}`,
                        {
                          name: editingUser.name,
                          phone: editingUser.phone || '',
                          role: editingUser.role,
                          email: editingUser.email || '',
                          username: editingUser.username || '',
                          storeId: editingUser.storeId || currentStore?.storeId || currentUser.storeId,
                          password: editingUser.password || 'dummy-temp-password',
                        },
                        {
                          headers: {
                            'X-User-Role': currentUser.role,
                          },
                        }
                      );
                      toast.success('User updated successfully');
                      setEditingUser(null);
                      loadUsers();
                    } catch (error) {
                      console.error('Failed to update user:', error);
                      toast.error(error.response?.data?.detail || 'Failed to update user');
                    }
                  }}
                  className="btn-bawarchi-primary"
                >
                  Save Changes
                </Button>
                <Button onClick={() => setEditingUser(null)} variant="outline">Cancel</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Reset Password Modal */}
      {resettingPasswordUser && (
        <Dialog open={!!resettingPasswordUser} onOpenChange={() => {
          setResettingPasswordUser(null);
          setNewPassword('');
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset Password for {resettingPasswordUser.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted">
                Set a new temporary password for this user. They should change it after first login.
              </p>
              <div>
                <label className="label-bawarchi">New Password (min 8 characters)</label>
                <div className="relative">
                  <input
                    type={showResetPassword ? "text" : "password"}
                    className="input-bawarchi pr-10"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetPassword(!showResetPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showResetPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={async () => {
                  if (newPassword.length < 8) {
                    toast.error('Password must be at least 8 characters');
                    return;
                  }
                  try {
                    await axios.post(`${API}/auth/admin-reset-password`, {
                      user_id: resettingPasswordUser.id,
                      new_password: newPassword
                    }, {
                      headers: {
                        'X-User-Role': currentUser.role
                      }
                    });
                    toast.success(`Password reset successfully for ${resettingPasswordUser.name}`);
                    setResettingPasswordUser(null);
                    setNewPassword('');
                  } catch (error) {
                    console.error('Password reset failed:', error);
                    toast.error(error.response?.data?.detail || 'Failed to reset password');
                  }
                }} className="btn-bawarchi-primary">Reset Password</Button>
                <Button onClick={() => {
                  setResettingPasswordUser(null);
                  setNewPassword('');
                }} variant="outline">Cancel</Button>
              </div>
              <p className="text-xs text-muted">
                Note: You must communicate the new password to the user directly.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Delete User Confirmation Modal */}
      {deletingUser && (
        <Dialog open={!!deletingUser} onOpenChange={() => setDeletingUser(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600">Delete User</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">
                  <strong>Warning:</strong> Are you sure you want to delete <strong>{deletingUser.name}</strong>?
                </p>
                <p className="text-sm text-red-700 mt-2">
                  This action cannot be undone.
                </p>
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={handleDeleteUser}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'Delete User'}
                </Button>
                <Button onClick={() => setDeletingUser(null)} variant="outline">Cancel</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Powered by Caterlog Footer */}
      <div className="text-center py-6 text-sm text-gray-500">
        Powered by <span className="font-semibold text-orange-600">Caterlog</span>
      </div>
    </div>
  );
}

// Super Admin Portal Component

export default TeamManagementPage;
